/**
 * @fileoverview Login-strings barrel — re-exports the canonical string bundles
 * plus `loadLoginStrings(i18nInstance, locales)`, which registers them under the
 * `login` namespace on a react-i18next instance.
 *
 * AAS-I18N-002 W2 / ruling 2 (Q-QF4-5). Mirrors `@puffin-core/page-shell`'s
 * `loadShellStrings` and `@puffin-core/onboarding-ui`'s `loadOnboardingStrings`.
 *
 * The ruled shape is deliberate: the alternative — threading a `t=` prop at the 12
 * `<LoginShell>` render sites and minting `login.*` leaves in ten app catalogs —
 * duplicates one corpus ten times, and app eleven forgets. One corpus, one place,
 * and a new app inherits the translation by construction.
 *
 * Per-app integration (authenticated apps and marketing portals alike):
 *   ```js
 *   import { loadLoginStrings } from '@puffin-core/login-shell';
 *   createAppI18n({ …, loadLoginStrings });
 *   ```
 * `createAppI18n` resolves the locale list from `Object.keys(resources)` the same
 * way it does for shell strings.
 */

import { DEFAULT_LOGIN_STRINGS } from './en.js';
import { LOGIN_STRINGS_ES } from './es.js';
import { LOGIN_NAMESPACE } from './namespace.js';

/**
 * The English bundle, exported under the `LOGIN_STRINGS_EN` name for symmetry with
 * `LOGIN_STRINGS_ES`. It is the SAME frozen object as `DEFAULT_LOGIN_STRINGS`,
 * which stays exported from `./en.js` because `resolveString` and every existing
 * consumer import it by that name.
 */
export const LOGIN_STRINGS_EN = DEFAULT_LOGIN_STRINGS;

export { LOGIN_STRINGS_ES };

const BUNDLES = Object.freeze({
  en: LOGIN_STRINGS_EN,
  es: LOGIN_STRINGS_ES,
});

const SUPPORTED_LOCALES = Object.freeze(Object.keys(BUNDLES));

export { LOGIN_NAMESPACE, SUPPORTED_LOCALES };

/**
 * Register one or more login-string bundles on a react-i18next instance.
 * Idempotent — registering the same locale twice is a no-op deep-merge.
 *
 * @param {object} i18nInstance - A react-i18next instance (the one the app mounts
 *   in `<I18nextProvider>`; NEVER the default singleton).
 * @param {string | string[]} [locales='en'] - Locale code(s) to load: `'en'`,
 *   `'es'`, or an array of any subset.
 * @returns {object} The same `i18nInstance`, for chaining.
 * @throws {Error} If the instance has no `addResourceBundle()`, or a requested
 *   locale is not in the supported set.
 */
// 2026-08-19: `resolveString` EXISTED in this package but was never exported from the barrel,
// so consumers could not reach the canonical fallback and improvised their own. Exported now;
// all seven `@puffin-core` string-bundle packages present the same surface.
export { resolveString } from './resolveString.js';

export function loadLoginStrings(i18nInstance, locales = 'en') {
  if (!i18nInstance || typeof i18nInstance.addResourceBundle !== 'function') {
    throw new Error('loadLoginStrings: expected a react-i18next instance with addResourceBundle()');
  }
  const list = Array.isArray(locales) ? locales : [locales];
  list.forEach((locale) => {
    const bundle = BUNDLES[locale];
    if (!bundle) {
      throw new Error(
        `loadLoginStrings: unsupported locale "${locale}". Supported: ${SUPPORTED_LOCALES.join(', ')}`,
      );
    }
    // Third arg = deep merge, fourth arg = do NOT overwrite, so an app that has
    // already injected its own `login.*` override keeps it.
    i18nInstance.addResourceBundle(locale, LOGIN_NAMESPACE, bundle, true, false);
  });
  return i18nInstance;
}
